<template>
  <div>
    <div class="tw-mt-3 tw-p-3">
      <form class="tw-w-full" @submit.prevent="submit">
        <div class="form-group">
          <div class="tw-w-full tw-px-3">
            <label class="form-label" for="year">
              Year <span class="required-field">&#42;</span>
            </label>
            <input
              id="year"
              v-model="form.year"
              v-validate="'required|date_format:yyyy'"
              v-focus
              name="year"
              data-vv-validate-on="input"
              data-vv-as="year"
              data-cy="holidays-year"
              class="form-control"
              type="text"
              autocomplete="off"
              placeholder="YYYY"
              inputmode="decimal"
              @input="getHolidays"
            />

            <p
              v-show="errors.has('year')"
              class="tw-mt-1 tw-text-red-700 tw-text-sm"
            >
              {{ errors.first('year') }}
            </p>
          </div>
        </div>

        <div class="form-group">
          <div class="tw-w-full tw-px-3">
            <label class="form-label" for="country">
              Country <span class="required-field">&#42;</span>
            </label>
            <VSelect
              id="country"
              v-model="form.country"
              v-validate="'required'"
              :options="sortedCountries"
              :show-labels="false"
              :max-height="130"
              :allow-empty="false"
              placeholder="Select a country to find holidays"
              name="country"
              data-vv-as="country"
              data-cy="holidays-country"
              label="name"
              track-by="name"
              :disabled="hasLocation"
              @input="getHolidays"
            >
              <template #singleLabel="props">
                <div
                  class="tw-flex tw-justify-start tw-items-center tw-content-center"
                >
                  <flag
                    :squared="false"
                    :iso="props.option.iso_code"
                    class="tw-mr-2"
                  />
                  <div
                    class="tw-text-base tw-font-normal"
                    data-cy="selected-holidays-country"
                  >
                    {{ props.option.name }}
                  </div>
                </div>
              </template>
              <template #noResult>
                <span>No items found.</span>
              </template>
              <template #option="props">
                <div
                  class="tw-flex tw-justify-start tw-items-center tw-content-center tw-leading-zero"
                >
                  <flag
                    :squared="false"
                    :iso="props.option.iso_code"
                    class="tw-mr-2"
                  />
                  <div
                    class="tw-text-base tw-font-normal tw-leading-none"
                    data-cy="selected-holidays-country"
                  >
                    {{ props.option.name }}
                  </div>
                </div>
              </template>
            </VSelect>
            <p
              v-show="errors.has('country')"
              class="tw-mt-1 tw-text-red-700 tw-text-sm"
            >
              {{ errors.first('country') }}
            </p>
          </div>
        </div>

        <div class="form-group">
          <div class="tw-w-full tw-px-3" data-cy="drpdown-region">
            <label class="form-label" for="state">
              Region <span class="required-field">&#42;</span>
            </label>
            <VSelect
              id="state"
              v-model="form.state"
              v-validate.disable="'required'"
              :options="states"
              :show-labels="false"
              :max-height="130"
              :loading="loading"
              :allow-empty="false"
              :disabled="loading || hasLocation"
              placeholder="Select region"
              name="state"
              data-vv-as="state"
              data-cy="drpdown-region-name"
            >
              <template #noResult>
                <span>No items found.</span>
              </template>
            </VSelect>

            <p
              v-show="errors.has('state')"
              class="tw-mt-1 tw-text-red-700 tw-text-sm"
            >
              {{ errors.first('state') }}
            </p>
          </div>
        </div>

        <div class="form-group">
          <div class="tw-w-full tw-px-3">
            <label class="form-label" for="location_name">
              Location name
            </label>
            <input
              id="location_name"
              v-model="form.locationName"
              v-validate.disable="'required'"
              name="location_name"
              data-vv-as="location name"
              class="form-control tw-mt-2"
              type="text"
              autocomplete="off"
              placeholder="Location name"
              :disabled="hasLocation"
            />

            <p
              v-show="errors.has('location_name')"
              class="tw-mt-1 tw-text-red-700 tw-text-sm"
            >
              {{ errors.first('location_name') }}
            </p>
          </div>
        </div>

        <div class="tw-flex tw-flex-wrap tw-mb-3">
          <div class="tw-w-full tw-px-3">
            <div class="tw-flex tw-justify-end">
              <button
                class="btn btn-blue tw-rounded-lg"
                data-cy="next-holiday-form"
                type="submit"
              >
                Save
              </button>
            </div>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { sortBy, debounce, toLower } from 'lodash-es'
import ValidateForm from '@/mixins/ValidatesForm'
import VSelect from 'vue-multiselect'

export default {
  name: 'LocationYearForm',

  components: { VSelect },

  mixins: [ValidateForm],

  props: {
    countries: {
      type: Array,
      required: true,
    },

    states: {
      type: Array,
      required: true,
    },

    selectedLocation: {
      type: Object,
      required: true,
    },

    selectedYear: {
      type: String,
      default: () => '',
    },

    loading: {
      type: Boolean,
      required: true,
    },

    customStates: {
      type: Object,
      required: true,
    },
  },

  data() {
    return {
      form: {
        country: null,
        year: this.selectedYear,
        state: this.selectedLocation.region,
        locationName: this.selectedLocation.name,
      },
    }
  },

  computed: {
    sortedCountries() {
      return sortBy(this.countries, 'name')
    },

    hasLocation() {
      return !!this.selectedLocation.id
    },
  },

  watch: {
    states(val) {
      if (
        !this.selectedLocation.region &&
        (val[0] === this.customStates.all ||
          val[0] === this.customStates.nationalHolidaysOnly)
      ) {
        this.form.state = val[0]
      }
    },

    'form.country': {
      handler(val) {
        if (this.hasLocation) return
        this.form.locationName = val.name
      },
    },

    'form.state': {
      handler(val) {
        if (this.hasLocation) return

        if (!this.form.country || val === '') return

        if (
          this.form.state === 'All' ||
          this.form.state === 'Exclude regional holidays'
        ) {
          this.form.locationName = this.form.country.name

          return
        }

        this.form.locationName = this.form.country.name + ' (' + val + ')'
      },
    },
  },

  mounted() {
    if (this.selectedLocation.country) {
      this.form.country = this.countries.find(country => {
        return toLower(country.name) === toLower(this.selectedLocation.country)
      })
    }

    if (!this.selectedLocation.region) {
      this.fetchHolidayStates()
    }
  },

  methods: {
    getHolidays() {
      if (this.hasLocation) return

      this.fetchHolidayStates()
      this.form.state = ''
    },

    fetchHolidayStates: debounce(function() {
      if (!this.form.country || !this.fields.year.valid) return

      this.$emit('location-year-changed', this.form)
    }, 200),

    async submit() {
      await this.validate()

      if (!this.valid) {
        return
      }

      if (!this.form.country.name) {
        this.errors.add({
          field: 'country',
          msg: 'Country field is required.',
        })

        return
      }

      this.$emit('submit', this.form)
    },
  },
}
</script>
